const textFor = (spell) => [
  spell?.description,
  spell?.desc,
  spell?.text,
  spell?.effect
].filter(Boolean).join(' ');

const damageComponentsFor = (spell) => {
  const candidates = Array.isArray(spell?.damage)
    ? spell.damage
    : (Array.isArray(spell?.attack?.damage) ? spell.attack.damage : []);

  if (candidates.length > 0) {
    return candidates.map((entry) => ({
      formula: entry?.dice || entry?.formula || entry?.damage || entry?.damageFormula || '',
      type: entry?.type || entry?.damageType || spell?.damageType || ''
    }));
  }

  const formula = typeof spell?.damage === 'string'
    ? spell.damage
    : (spell?.damageFormula || spell?.attack?.damageFormula || '');
  return formula ? [{ formula, type: spell?.damageType || spell?.attack?.damageType || '' }] : [];
};

const hasSpellAttack = (spell, text) => {
  const attackType = String(spell?.attack?.type || spell?.attackType || '').toLowerCase();
  return spell?.requiresAttackRoll === true
    || spell?.attackRoll === true
    || attackType.includes('spell')
    || /\b(?:make|makes)\s+(?:a|an)\s+(?:melee|ranged)?\s*spell attack\b/i.test(text)
    || /\b(?:melee|ranged) spell attack\b/i.test(text);
};

const hasSavingThrow = (spell, text) => Boolean(
  spell?.save
  || spell?.savingThrow
  || spell?.saveType
  || /\b(?:strength|dexterity|constitution|intelligence|wisdom|charisma) saving throw\b/i.test(text)
  || (/\bsaving throw\b/i.test(text) && /\bDC\s*\d+\b/i.test(text))
);

export const classifySpellResolution = (spell, resolverMeta = null) => {
  const text = textFor(spell);
  const damageComponents = damageComponentsFor(spell);
  const missingDamageTypes = damageComponents.filter((entry) => (
    entry.formula && !String(entry.type || '').trim()
  ));
  const attackRoll = hasSpellAttack(spell, text);
  const savingThrow = hasSavingThrow(spell, text);
  const automatic = Boolean(
    spell?.autoHit === true
    || spell?.automaticHit === true
    || resolverMeta?.kind === 'auto_hit_multi_dart'
    || (resolverMeta?.engineSupported === true && resolverMeta?.kind === 'auto_hit')
  );
  const healing = Boolean(spell?.healing || spell?.healingFormula);
  const explicitlyNarrative = spell?.utilitySpell === true || spell?.spellResolution === 'utility';
  const engineSupported = resolverMeta?.engineSupported === true;

  if (missingDamageTypes.length > 0) {
    return {
      supported: false,
      mode: 'gm_help',
      reason: 'Damage is present, but one or more damage types are missing.',
      damageComponents
    };
  }
  if (attackRoll) return { supported: true, mode: 'attack_roll', damageComponents };
  if (savingThrow) return { supported: true, mode: 'saving_throw', damageComponents };
  if (automatic) return { supported: true, mode: 'automatic', damageComponents };
  if (healing) return { supported: true, mode: 'healing', damageComponents };
  if (engineSupported) return { supported: true, mode: resolverMeta.kind || 'structured', damageComponents };
  if (explicitlyNarrative) return { supported: true, mode: 'utility', damageComponents };

  return {
    supported: false,
    mode: 'gm_help',
    reason: damageComponents.length > 0
      ? 'The spell deals damage without a supported attack roll, saving throw, or automatic-hit rule.'
      : 'The spell needs a Game Master decision or a structured engine rule to resolve its effects.',
    damageComponents
  };
};

export const buildGameMasterSpellResolutionRequest = (spell, classification) => ({
  kind: 'spell_resolution',
  spellName: spell?.name || spell?.spellName || 'Unknown Spell',
  spellLevel: spell?.spellLevel ?? spell?.level ?? null,
  reason: classification?.reason || 'This spell needs Game Master help to resolve.',
  description: spell?.description || spell?.desc || '',
  suggestedResolvers: ['human', 'ai']
});

export default classifySpellResolution;
